import { DecimalInput } from '@framework/form/inputs/DecimalInput.tsx';
import { SelectInput } from '@framework/form/inputs/SelectInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { SecondaryCompetenceTypeAsyncSelect } from '../../../../../schema/secondary-competence-types/SecondaryCompetenceTypeAsyncSelect.tsx';
import { useMapLayerAttributes } from '../../components/useMapLayerAttributes.ts';
import { useWatchedMapLayerSource } from '../useWatchedMapLayerSource.ts';

export interface IMapLayerCompetenceConfigInputProps {
  prefix: string;
}

const ROLE_OPTIONS = [
  { label: 'Primaria', value: 'primary' },
  { label: 'Secondaria', value: 'secondary' },
];

/**
 * Configurazione competenza per un singolo layer della mappa.
 */
export function MapLayerCompetenceConfigInput(
  props: IMapLayerCompetenceConfigInputProps,
) {
  const { prefix } = props;

  const role = useWatch({ name: `${prefix}competence.role` });
  const layerSource = useWatchedMapLayerSource(prefix);

  const { availableAttributes, isLoading } = useMapLayerAttributes(layerSource);

  const attributeOptions = useMemo(
    () =>
      availableAttributes.map(attribute => ({
        label: attribute.alias
          ? `${attribute.name} (${attribute.alias})`
          : attribute.name,
        value: attribute.name,
      })),
    [availableAttributes],
  );

  return (
    <>
      <FormItem label="Ruolo" name={`${prefix}competence.role`}>
        <SelectInput name={`${prefix}competence.role`} options={ROLE_OPTIONS} />
      </FormItem>
      <FormItem
        label="Proprietà identificativa"
        name={`${prefix}competence.identifyingPropertyName`}
        help="Nome della proprietà che identifica il codice competenza nelle feature del layer"
      >
        <SelectInput
          name={`${prefix}competence.identifyingPropertyName`}
          mode="tags"
          loading={isLoading}
          options={attributeOptions}
          transformValue={value => (value ? [value] : [])}
          onChange={(value, _option, onChange) => {
            const list = Array.isArray(value) ? value : [value];
            onChange(list.length > 0 ? list[list.length - 1] : null);
          }}
          placeholder="Seleziona o digita il nome della proprietà"
        />
      </FormItem>
      {role === 'secondary' && (
        <FormItem
          label="Tipo competenza secondaria"
          name={`${prefix}competence.secondaryCompetenceTypeId`}
        >
          <SecondaryCompetenceTypeAsyncSelect
            name={`${prefix}competence.secondaryCompetenceTypeId`}
            allowClear
            placeholder="Seleziona tipo competenza secondaria"
          />
        </FormItem>
      )}
      <FormItem
        label="Tolleranza area (mq)"
        name={`${prefix}competence.areaTolerance`}
        help="Area minima in metri quadri sotto la quale l'intersezione viene ignorata"
      >
        <DecimalInput name={`${prefix}competence.areaTolerance`} min={0} />
      </FormItem>
    </>
  );
}
